import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Spinner } from '@/components/common/Loading';
import logoMark from '@/assets/logo-mark.png';

/** 퇴장 시간. index.css 의 lc-fade-out(0.18s) 과 맞춘다 */
const EXIT_DURATION = 190;

interface BootScreenProps {
  message?: string;
  /** 퇴장 중. 셸이 드러나는 동안 클릭을 가로채지 않는다 */
  fading?: boolean;
}

/**
 * 셸 진입 화면.
 *
 * 화면 전체를 거의 불투명한 Dim 으로 덮는다. 사이드바·헤더·본문은 서로 다른
 * 시점에 준비되므로, 덮지 않으면 빈 사이드바 → 헤더 → 본문이 차례로 튀어나오는
 * 과정이 그대로 보인다. 다 그려진 뒤 이 막이 걷히면서 셸이 한 번에 드러난다.
 *
 * 진입의 모든 단계가 이 화면을 공유한다 — 청크 다운로드(`App.tsx` 의 Suspense),
 * 프로필 확인(`RoleRoute`), 포털 세션·착지(`PortalApp`·`PortalLanding`),
 * 셸 첫 조회(`ShellBootGate`). 단계마다 모양이 달라지면 한 번의 진입이 여러 번
 * 깜빡인 것처럼 보이기 때문에, 여기서 모양을 하나로 묶는다.
 *
 * 영역 단위 대기에는 이걸 쓰지 말 것 — 목록·카드 자리에는 `Loading`,
 * 이미 그려진 내용 위에는 `LoadingOverlay` 가 맞다.
 */
export const BootScreen = ({
  message = '화면을 준비하는 중...',
  fading = false,
}: BootScreenProps) => (
  <div
    className={clsx(
      // 전체 화면 로딩은 모달과 같은 층위에 둔다 (index.css 의 z 층위 참고)
      'fixed inset-0 z-[var(--z-modal)] flex items-center justify-center',
      // 반투명하게 두면 반쯤 그려진 셸이 비쳐 보여 덮는 의미가 없다
      'bg-gray-50/97 backdrop-blur-[4px]',
      fading ? 'animate-fade-out pointer-events-none' : 'animate-fade-in',
    )}
    role='status'
    aria-live='polite'
    aria-busy={!fading}
  >
    <div className='flex flex-col items-center gap-3.5'>
      <img
        src={logoMark}
        alt=''
        aria-hidden='true'
        className='w-12 h-12 object-contain animate-breathe'
      />
      <Spinner size='sm' />
      <p className='text-sm text-gray-500'>{message}</p>
    </div>
  </div>
);

interface ShellBootGateProps {
  /** 셸을 드러내도 되는 상태인가. `useShellReady()` 가 판정한다 */
  ready: boolean;
  message?: string;
}

/**
 * 셸이 준비될 때까지 진입 화면을 붙잡고 있다가, 준비되면 fade-out 을 마친 뒤
 * 스스로 언마운트한다. `ready` 가 되는 즉시 지우면 셸이 툭 나타나므로 한 번
 * 걷히는 동작을 보여 준다.
 */
export const ShellBootGate = ({ ready, message }: ShellBootGateProps) => {
  const [mounted, setMounted] = useState(true);

  useEffect(() => {
    if (!ready) return;

    const timer = window.setTimeout(() => setMounted(false), EXIT_DURATION);
    return () => window.clearTimeout(timer);
  }, [ready]);

  if (!mounted) return null;

  return <BootScreen message={message} fading={ready} />;
};
